<template>
  <v-sheet v-hotkey="keymap" :elevation="floatMode ? 4 : 0" class=" pa-2">
    <div class="text-h6 text-center">
      {{ $t("diagnosis.visualization.selectTitle") }}
    </div>
    <v-list dense class="pt-3" ref="annotationClassList"></v-list>
    <v-overlay
      :value="loadingDiagnosis"
    >
      <v-progress-circular
          indeterminate
          size="64"
        ></v-progress-circular>
    </v-overlay>
    <v-divider></v-divider>
    <div class="d-flex">
      <v-col class="px-0">
        <div class="text-h6 text-center">
          {{ $t("diagnosis.visualization.wsiListTitle") }}
        </div>
        <v-data-table
          dense
          disable-filtering
          disable-pagination
          disable-sort
          hide-default-header
          hide-default-footer
          :items-per-page="100"
          :headers="headers"
          :items="slidesWithDiagnosis"
          @click:row="selectItem"
        >
          <template v-slot:item="{ item }">
            <tr>
              <!-- <td :class="slide.id === item.id ? 'active' : ''"> -->
              <td 
                v-if="item.diagnosis"
                :class="[slide.id === item.id ? 'current': '', itemClass(item), 'text-center']"
              >
                <!-- {{ $t("report." + item.diagnosis.annotation_class.class_name.toLowerCase()) }} -->
                {{ item.id }} - {{ item.diagnosis.annotation_class.class_name }}
              </td>
              <td
                v-else
                :class="[slide.id === item.id ? 'current': '', 'pending', 'text-center']"
              >
                {{ item.id }} - {{ $t("diagnosis.visualization.pending") }}
              </td>
              <!-- <td
                class="mx-0 px-0"
              >
                <v-btn
                  block
                  tile
                  elevation="0"
                  @click="selectItem(item)"
                >
                <v-icon
                  dense
                >
                  mdi-arrow-right
                </v-icon>
                </v-btn>
              </td> -->
            </tr>
          </template>

        </v-data-table>
      </v-col>
    </div>
    <v-divider></v-divider>
    <div class="d-flex">
      <v-col class="px-0 mx-0">
        <v-tooltip right>
          <template v-slot:activator="{ on, attr }">
            <v-btn
              block
              tile
              :disabled="disabledNext"
              elevation="0"
              v-on="on"
              v-bind="attr"
              @click="$emit('next-slide')"
            >
              {{ $t("diagnosis.visualization.nextButton") }}
              <v-icon
                right
                light
              >
                mdi-arrow-right-thick
              </v-icon>
            </v-btn>
          </template>
          <span>
            {{ $t("diagnosis.visualization.nextButtonTooltip") }}
          </span>
        </v-tooltip>
        <v-tooltip right>
          <template v-slot:activator="{ on, attr }">
            <v-btn
              block
              tile
              :disabled="disabledPrevious"
              elevation="0"
              v-on="on"
              v-bind="attr"
              @click="$emit('previous-slide')"
            >
              <v-icon
                left
                light
              >
                mdi-arrow-left-thick
              </v-icon>
              {{ $t("diagnosis.visualization.previousButton") }}
            </v-btn>
          </template>
          <span>
            {{ $t("diagnosis.visualization.previousButtonTooltip") }}
          </span>
        </v-tooltip>
        <v-tooltip right>
          <template v-slot:activator="{ on, attr }">
            <v-btn
              block
              tile
              :class="finalizeSuccess ? 'success' : 'warning'"
              elevation="0"
              v-on="on"
              v-bind="attr"
              @click="$emit('finalize', finalizeSuccess)"
            >
              {{ $t("diagnosis.visualization.finalize") }}
            </v-btn>
          </template>
          <span>
              {{ $t("diagnosis.visualization.finalizeTooltip") }}
          </span>
        </v-tooltip>
      </v-col>
    </div>
  </v-sheet>
</template>

<script>
import Vue from "vue";
import AnnotationClassControl from "@/components/slide/controls/AnnotationClassControl";
import DrawAnnotationControl from "@/viewer/DrawAnnotationControl";
import SelectControl from "@/viewer/SelectControl";
import DeleteControl from "@/viewer/DeleteControl";
import ManipulationControl from "@/components/slide/controls/ManipulationControl";
import { deleteAnnotation, storeAnnotation } from "@/api/craiyon";
import { deleteResource } from "@/api/fhir";
import { uuidv4 } from "@/utils/deeppath-utils";
import {
  FEATURE_ANNOTATION_ID,
  FEATURE_OBSERVATION_ID,
  FEATURE_STATUS
} from "@/constants";
import { FIND } from "@/store/modules/annotationClass";
import { getAnnotationsForSlide } from "@/api/craiyon";
import { mapGetters } from "vuex";

import { fromExtent } from 'ol/geom/Polygon';
import Feature from 'ol/Feature';

export default {
  props: {
    slide: Object,
    slides: Array,
    viewer: Object,
    observations: Array,
    annotations: Array,
    floatMode: Boolean,
    currentSelectedRawAnnotation: Object,
  },
  data() {
    return {
      dialogNewAnnotation: false,
      dialogNewAnnotationData: {
        feature: null,
        description: null
      },
      currentActiveControl: null,
      manipulationControls: [],
      currentSelectedAnnotation: null,
      floatModeActive: false,
      runningOperationsStack: [],
      drawControls: [],
      currentFeatures: [],
      loadingDiagnosis: false,
      slidesWithDiagnosis: [],
      disabledPrevious: false,
      disabledNext: false,
      finalizeSuccess: false,
      headers: [
        // {
        //   text: "ID",
        //   value: "id"
        // },
        { 
          text: "Diagnosis",
          value: "diagnosis",
          align: "text-center"
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      annotationClass: FIND,
    }),
    map() {
      return this.viewer.map;
    },
    keymap() {
      return {
        d: this.toggleManipulationDelete,
        e: this.toggleManipulationModify,
        s: this.toggleManipulationSelect
      };
    },
    hasRunningOperations() {
      const val = this.runningOperationsStack.length > 0;
      return val;
    },
  },
  watch: {
    hasRunningOperations(val) {
      this.$emit("running-operations:changed", val);
    },
    floatModeActive(val) {
      this.$emit("float-mode:changed", val);
    },
    currentSelectedAnnotation(val) {
      if (val === null || val === undefined) {
        this.$emit("annotation:hide-details");
      }
    }
  },
  methods: {
    selectItem(slide) {
      this.$emit('go-to-slide', slide);
    },
    itemClass(slide) {
      if (slide.diagnosis)
        return slide.diagnosis.annotation_class.class_name.toLowerCase()
      return null
    },
    async getDiagnosis() {
      this.slidesWithDiagnosis = [];
      const currentIndex = this.slides.indexOf(this.slide);
      const annotation = await getAnnotationsForSlide(
        this.token,
        this.slides[currentIndex].id
      )
      this.slidesWithDiagnosis.push({
        id: this.slides[currentIndex].id,
        fhir_identifier: this.slides[currentIndex].fhir_identifier,
        annotation_class: this.slides[currentIndex].annotation_class,
        project: this.slides[currentIndex].project,
        status: this.slides[currentIndex].status,
        diagnosis: annotation.length > 0 ? annotation[0] : undefined
      })
      this.disabledPrevious = currentIndex === 0;
      this.disabledNext = currentIndex + 1 === this.slides.length;

      // Check if all diagnosis are finished
      let total_diagnosis = 0;
      for (let index = 0; index < this.slides.length; index++) {
        const annotation = await getAnnotationsForSlide(
          this.token,
          this.slides[index].id
        )
        total_diagnosis += annotation.length > 0 ? 1 : 0;
      }
      this.finalizeSuccess = total_diagnosis === this.slides.length;
    },
    async clickDiagnosis(e, componentInstance) {
      this.loadingDiagnosis = true;
      this.deactivateCurrentControl();
      this.toggleControl(e.annotationClass.annotation_geometry);
      this.currentActiveControl = componentInstance;
      componentInstance.active = true;
      const extent = this.viewer.map.getView().getProjection().getExtent();
      const feature = new Feature({
        geometry: fromExtent(extent)
      });
      const features = this.currentFeatures.concat(this.annotations);
      const annotationClassId = e.annotationClass.id;
      if (features.length > 0) {
        await this.deleteDiagnosis(features[0]);
        this.currentFeatures = [];
      }
      await this.storeDiagnosis(annotationClassId, feature);
      await this.getDiagnosis();
      this.loadingDiagnosis = false;
      return
    },
    async deleteDiagnosis(feature) {
      this.runningOperationsStack.push(true);
      const id = feature.get(FEATURE_ANNOTATION_ID);
      await deleteAnnotation(this.token, id)
      this.runningOperationsStack.pop();
    },
    async storeDiagnosis(annotationClassId, feature) {
      this.runningOperationsStack.push(true);
      if (
        this.dialogNewAnnotationData.description === null ||
        this.dialogNewAnnotationData.description === undefined
      ) {
        this.dialogNewAnnotationData.description = "";
      }
      const annotation = {
        wsiId: this.slide.id,
        geometry: feature,
        slide: this.slide.fhir_identifier,
        annotationClassId: annotationClassId,
        description: this.dialogNewAnnotationData.description,
        project: this.slide.project
      };
      // emulate observation and assign a temporary id
      // the observation will be replaced by the one coming from the server
      // once it was persisted
      const uuid = uuidv4();
      feature.set(FEATURE_OBSERVATION_ID, uuid);
      this.$emit("annotation:created", {
        resourceType: "Observation",
        id: uuid,
        code: {
          coding: [
            {
              code: annotationClassId
            }
          ]
        },
        note: [
          {
            text: this.dialogNewAnnotationData.description
          }
        ]
      });
      // reset
      // this.resetDialogNewAnnotation();
      // this.deactivateCurrentControl();

      let res = await storeAnnotation(this.token, annotation);
      feature.set(FEATURE_ANNOTATION_ID, res.id);
      feature.set(FEATURE_OBSERVATION_ID, res.observation_id);
      feature.set(FEATURE_STATUS, "created");
      this.currentFeatures.push(feature)

      this.$emit("annotation:stored", { id: res.observation_id, uuid });
      this.runningOperationsStack.pop();
    },
    toggleManipulationDelete() {
      this.activateManipulationControl("delete");
    },
    toggleManipulationModify() {
      this.activateManipulationControl("modify");
    },
    toggleManipulationSelect() {
      this.activateManipulationControl("select");
    },
    activateManipulationControl(action) {
      // deactivate draw controls
      this.drawControls.forEach(e => e.deactivate());
      const component = this.manipulationControls.find(
        e => e.action === action
      );
      component.click();
    },
    renderAnnotationClassControls() {
      const annotationClassControlList = this.$refs.annotationClassList.$el;
      const source = this.viewer.editLayer.getSource();
      const layer = this.viewer.editLayer;

      for (let ac of this.slide.annotation_class) {
        const annotationClass = this.annotationClass(ac);
        const componentClass = Vue.extend(AnnotationClassControl);
        const componentInstance = new componentClass({
          propsData: {
            annotationClass
          }
        });
        componentInstance.$mount();

        const annotationControl = new DrawAnnotationControl({
          optElement: componentInstance.$el,
          optTarget: annotationClassControlList,
          optSource: source,
          optType: annotationClass.annotation_geometry,
          optFillColor: annotationClass.fill_color,
          optStrokeColor: annotationClass.color,
          optCallback: this.endDrawAnnotation,
          optAnnotationClass: annotationClass,
          layer
        });
        this.drawControls.push(annotationControl);
        componentInstance.control = annotationControl;
        annotationControl.setMap(this.map);
        componentInstance.$on("click", e =>
          // this.onClickControl(e, componentInstance)
          this.clickDiagnosis(e, componentInstance)
        );
      }
    },
    renderAnnotationManipulationControls() {
      const manipulationControls = [
        {
          callback: this.selectAnnotation,
          control: SelectControl,
          props: {
            tooltip: "Select annotation",
            action: "select"
          }
        },
        // {
        //   callback: this.modifyAnnotation,
        //   control: ModifyControl,
        //   props: {
        //     tooltip: "Modify annotation",
        //     action: "modify"
        //   }currentSelectedRawAnnotation
        // },
        {
          callback: this.deleteAnnotation,
          control: DeleteControl,
          props: {
            tooltip: "Delete annotations",
            action: "delete"
          }
        }
      ];
      this.manipulationControls = [];
      const target = this.$refs["annotationManipulationControls"];
      const source = this.viewer.editLayer.getSource();
      for (let mc of manipulationControls) {
        const componentClass = Vue.extend(ManipulationControl);
        const componentInstance = new componentClass({
          propsData: mc.props
        });
        componentInstance.$mount();
        this.manipulationControls.push(componentInstance);
        const annotationControl = new mc.control({
          element: componentInstance.$el,
          target,
          source,
          callback: mc.callback
        });
        annotationControl.setMap(this.map);
        componentInstance.control = annotationControl;
        componentInstance.$on("click", e =>
          this.onClickControl(e, componentInstance)
        );
      }
    },
    onClickControl(e, componentInstance) {
      this.currentSelectedAnnotation = null;
      if (this.currentActiveControl !== null) {
        // deactivate the current control and remove the highlighting
        this.deactivateCurrentControl();
        // if button is pressed a second time
        if (componentInstance.type === "mutation") {
          if (this.toggleControl(e.action)) {
            return;
          }
        }
        if (componentInstance.type === "annotationClass") {
          if (this.toggleControl(e.annotationClass.annotation_geometry)) {
            return;
          }
        }
      }

      this.currentActiveControl = componentInstance;
      componentInstance.active = true;
      e.control.activate();
    },

    toggleControl(currentAction) {
      this.currentSelectedAnnotation = null;
      if (!this.currentActiveControl) {
        return false;
      }
      if (currentAction === this.currentActiveControl.action) {
        this.currentActiveControl.active = false;
        this.currentActiveControl.control.deactivate();
        this.currentActiveControl = null;
        return true;
      }
      return false;
    },
    deactivateCurrentControl() {
      if (this.currentActiveControl !== null) {
        // deactivate the current control and remove the highlighting
        this.currentActiveControl.active = false;
        this.currentActiveControl.control.deactivate();
        const currentAction = this.currentActiveControl.action;
        if (currentAction === "select" || currentAction === "delete") {
          // remove selected features
          this.currentActiveControl.control.interaction.getFeatures().clear();
        }
      }
      this.currentSelectedAnnotation = null;
    },
    endDrawAnnotation(e) {
      console.log(e)
      if (
        e.annotationClass.annotation_geometry === "Polygon" &&
        this.arrayIntersect(e.target.sketchLineCoords_)
      ) {
        //TODO show notification
        //TODO fix uncaught error
        this.cancelAnnotation();
      } else {
        this.dialogNewAnnotation = true;
        this.dialogNewAnnotationData.feature = e;
      }
      // TODO focus on field name
      // this.$refs["inputAnnotationName"].$refs.input.focus();
    },
    selectAnnotation(e) {
      // interaction.getFeatures().select()
      if (e.selected.length === 0) {
        this.currentSelectedAnnotation = null;
        return;
      }
      const observationId = e.selected[0].get(FEATURE_OBSERVATION_ID);
      const observation = this.observations.find(e => e.id === observationId);
      this.currentSelectedAnnotation = observation;
      this.$emit("annotation:selected", observation);
    },
    async storeAnnotation() {
      this.runningOperationsStack.push(true);
      const e = this.dialogNewAnnotationData.feature;
      if (
        this.dialogNewAnnotationData.description === null ||
        this.dialogNewAnnotationData.description === undefined
      ) {
        this.dialogNewAnnotationData.description = "";
      }
      const feature = e.feature;
      const annotation = {
        wsiId: this.slide.id,
        geometry: feature,
        slide: this.slide.fhir_identifier,
        annotationClassId: e.annotationClass.id,
        description: this.dialogNewAnnotationData.description,
        project: this.slide.project
      };
      // emulate observation and assign a temporary id
      // the observation will be replaced by the one coming from the server
      // once it was persisted
      const uuid = uuidv4();
      feature.set(FEATURE_OBSERVATION_ID, uuid);
      this.$emit("annotation:created", {
        resourceType: "Observation",
        id: uuid,
        code: {
          coding: [
            {
              code: e.annotationClass.id
            }
          ]
        },
        note: [
          {
            text: this.dialogNewAnnotationData.description
          }
        ]
      });
      // reset
      this.resetDialogNewAnnotation();
      // this.deactivateCurrentControl();

      let res = await storeAnnotation(this.token, annotation);
      console.log(res);
      feature.set(FEATURE_ANNOTATION_ID, res.id);
      feature.set(FEATURE_OBSERVATION_ID, res.observation_id);
      feature.set(FEATURE_STATUS, "created");

      this.$emit("annotation:stored", { id: res.observation_id, uuid });
      this.runningOperationsStack.pop();
    },
    async deleteAnnotation(e) {
      return new Promise((resolve, reject) => {
        if (e.selected.length === 0) {
          reject();
          return;
        }
        const status = e.selected[0].get(FEATURE_STATUS);

        if (status !== "created") {
          // TODO show some notification
          reject();
          return;
        }
        this.runningOperationsStack.push(true);
        const id = e.selected[0].get(FEATURE_ANNOTATION_ID);
        deleteAnnotation(this.token, id).then(data => resolve());
        this.runningOperationsStack.pop();
      });
    },
    resetDialogNewAnnotation() {
      this.dialogNewAnnotationData = {};
      this.dialogNewAnnotation = false;
    },
    cancelAnnotation() {
      const e = this.dialogNewAnnotationData.feature.feature;
      this.viewer.editLayer.getSource().removeFeature(e);
      this.resetDialogNewAnnotation();
      this.deactivateCurrentControl();
    },
    modifyAnnotation(e) {
      console.log("modify annotation on server", e);
    },
    intersects(a, b, c, d, p, q, r, s) {
      var det, gamma, lambda;
      det = (c - a) * (s - q) - (r - p) * (d - b);
      if (det === 0) {
        return false;
      } else {
        lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det;
        gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det;
        return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
      }
    },
    arrayIntersect(array) {
      let tmpArray = array;
      tmpArray.push(array[0]);
      for (let i = 0; i < tmpArray.length - 1; i += 1) {
        for (let j = i + 2; j < tmpArray.length - 1; j += 1) {
          // Anchor line
          const point_0 = tmpArray[i];
          const point_1 = tmpArray[i + 1];
          // Test line
          const point_2 = tmpArray[j];
          const point_3 = tmpArray[j + 1];
          if (
            this.intersects(
              point_0[0],
              point_0[1],
              point_1[0],
              point_1[1],
              point_2[0],
              point_2[1],
              point_3[0],
              point_3[1]
            )
          ) {
            return true;
          }
        }
      }
      return false;
    }
  },
  async mounted() {
    await this.getDiagnosis();
    console.log(this.slides)
    console.log(this.slidesWithDiagnosis)
    this.renderAnnotationClassControls();
    this.floatModeActive = this.floatMode;
  }
};
</script>

<style scoped>
>>>.adenocarcinoma {
  background-color: #ff0000 !important;
}
>>>.adenoma {
  background-color: #F2C511 !important;
}
>>>.other {
  background-color: #F39C19 !important;
}
>>>.normal {
  background-color: #27AF60 !important;
}
>>>.active {
  background-color: #BDBDBD !important;
}
>>>.pending {
  background-color: lightgray !important;
}
>>>.current {
  border: 2px solid blue !important;
}
</style>
